/**
 *
 * Copyright © 2026 Ping Identity Corporation. All right reserved.
 *
 * This software may be modified and distributed under the terms
 * of the MIT license. See the LICENSE file for details.
 *
 **/

import { protect } from '@forgerock/protect';
import { get, writable } from 'svelte/store';
import { z } from 'zod';

import type {
  Protect,
  ProtectConfig as SdkProtectConfig,
  SignalsInitializationOptions,
} from '@forgerock/protect/types';
import type { Readable } from 'svelte/store';

/**
 * Public API config schema. Requires `envId`; types the fields we know.
 *
 * `z.looseObject` keeps unlisted keys (unlike `z.object`, which drops them). We list only
 * a subset of the fields AM sends, and the SDK forwards them all to `_pingOneSignals.init()`
 * unvalidated — so extra keys like `customHost` must pass through untouched.
 * @see https://zod.dev/api#zlooseobject (`z.looseObject` — allow unknown keys through)
 * @see https://github.com/ForgeRock/ping-javascript-sdk/blob/@forgerock/protect@2.1.0/packages/protect/src/lib/protect.types.ts#L20 (SDK ProtectConfig — only envId required)
 * @see https://github.com/ForgeRock/ping-javascript-sdk/blob/@forgerock/journey-client@2.1.0/packages/journey-client/src/lib/callbacks/ping-protect-initialize-callback.ts#L38-L49 (AM callback fields we don't list)
 */
export const protectConfigSchema = z.looseObject({
  envId: z.string({
    error: (issue) =>
      issue.input === undefined
        ? 'Setting `envId` is required (this is generated by the Zod library).'
        : '`envId` must be a string (this is generated by the Zod library).',
  }),
  waitForWindowLoad: z.boolean().optional(),
  hubUrl: z.string().optional(),
  disableHub: z.boolean().optional(),
  deviceAttributesToIgnore: z.array(z.string()).optional(),
  behavioralDataCollection: z.boolean().optional(),
  disableTags: z.boolean().optional(),
  externalIdentifiers: z.record(z.string(), z.string()).optional(),
  universalDeviceIdentification: z.boolean().optional(),
  agentIdentification: z.boolean().optional(),
  agentTimeout: z.number().optional(),
  agentPort: z.number().optional(),
  consoleLogEnabled: z.boolean().optional(),
});

/**
 * Schema for callback-provided PingOne Signals initialization options.
 * The Ping JS SDK defines this as an arbitrary string key-value map.
 */
export const signalsInitializationOptionsSchema = z.record(z.string(), z.string());

/**
 * Schema for all supported Protect start config shapes.
 * This replaces the previous manual `'envId' in config` branching.
 */
export const protectStartConfigSchema = z.union([
  protectConfigSchema,
  signalsInitializationOptionsSchema,
]);

export type ProtectConfig = z.infer<typeof protectConfigSchema>;
export type ProtectStartConfig = z.infer<typeof protectStartConfigSchema>;

export interface ProtectStore extends Readable<Protect | null> {
  start: (
    config: ProtectConfig | SignalsInitializationOptions,
  ) => Promise<void | { error: string }>;
  getData: () => Promise<string | { error: string }>;
  pauseBehavioralData: () => void | { error: string };
  resumeBehavioralData: () => void | { error: string };
}

const protectClientStore = writable<Protect | null>(null);

/**
 * @function start - Creates (or recreates) the shared Protect client and starts it.
 * @returns {Promise<void | { error: string }>}
 */
function start(config: ProtectConfig | SignalsInitializationOptions) {
  // Protect is runtime code, so failures return `{ error }` for the caller to surface — never throw.
  const parseResult = protectStartConfigSchema.safeParse(config);
  if (!parseResult.success) {
    return Promise.resolve({ error: parseResult.error.message });
  }
  const safeConfig = parseResult.data as SdkProtectConfig | SignalsInitializationOptions;
  const protectClient = protect(safeConfig);
  protectClientStore.set(protectClient);
  return protectClient.start();
}

/**
 * @function getData - Returns device data from the shared Protect client.
 * @returns {Promise<string | { error: string }>}
 */
function getData() {
  const protectClient = get(protectClientStore);
  if (!protectClient) {
    return Promise.resolve({ error: 'Protect client not initialized' });
  }
  return protectClient.getData();
}

/**
 * @function pauseBehavioralData - Pauses behavioral data collection on the shared client.
 * @returns {void | { error: string }}
 */
function pauseBehavioralData() {
  const protectClient = get(protectClientStore);
  if (!protectClient) {
    return { error: 'Protect client not initialized' };
  }
  return protectClient.pauseBehavioralData();
}

/**
 * @function resumeBehavioralData - Resumes behavioral data collection on the shared client.
 * @returns {void | { error: string }}
 */
function resumeBehavioralData() {
  const protectClient = get(protectClientStore);
  if (!protectClient) {
    return { error: 'Protect client not initialized' };
  }
  return protectClient.resumeBehavioralData();
}

export const protectStore: ProtectStore = {
  subscribe: protectClientStore.subscribe,
  start,
  getData,
  pauseBehavioralData,
  resumeBehavioralData,
};
